/**
 * RunAfterRenderer - Minimal run after configuration
 *
 * Clean design: compact dependency rows with inline checkboxes
 */

import { useState } from 'react';
import { makeStyles, tokens, Text, Checkbox, Button, Combobox, Option } from '@fluentui/react-components';
import { AddRegular, DismissRegular } from '@fluentui/react-icons';
import type { RunAfterSettings, RunAfterStatus } from '@/types/nodeSettings';
import type { SettingRendererProps } from './registry';
import { registerSettingRenderer } from './registry';

const useStyles = makeStyles({
  container: {
    padding: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
  },
  label: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    marginBottom: tokens.spacingVerticalS,
    display: 'block',
  },
  dependencyList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  dependencyItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalXS,
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalXS,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  dependencyName: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    minWidth: '120px',
  },
  statusGroup: {
    display: 'flex',
    flex: 1,
    gap: tokens.spacingHorizontalS,
  },
  emptyState: {
    padding: tokens.spacingVerticalS,
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
    fontSize: tokens.fontSizeBase200,
  },
  addButton: {
    marginTop: tokens.spacingVerticalS,
  },
});

const RUN_AFTER_STATUSES: { value: RunAfterStatus; label: string }[] = [
  { value: 'Succeeded', label: 'Succeeded' },
  { value: 'Failed', label: 'Failed' },
  { value: 'Skipped', label: 'Skipped' },
  { value: 'TimedOut', label: 'Timed Out' },
];

export interface RunAfterRendererProps extends SettingRendererProps<RunAfterSettings> {
  label?: string;
  /** Available predecessor actions that can be selected (already filtered for cycles) */
  availableActions?: string[];
  /** @deprecated Use inline combobox instead */
  onAddDependency?: () => void;
}

/**
 * RunAfterRenderer component for run after configuration
 */
export const RunAfterRenderer = ({
  setting,
  onChange,
  readOnly = false,
  label = 'Run After',
  availableActions = [],
}: RunAfterRendererProps) => {
  const styles = useStyles();
  const [isAdding, setIsAdding] = useState(false);

  // Don't render if setting is not supported
  if (!setting.isSupported) {
    return null;
  }

  const currentSettings = setting.value ?? {};
  const dependencyNames = Object.keys(currentSettings);

  const handleStatusChange = (actionName: string, status: RunAfterStatus, checked: boolean) => {
    const currentStatuses = currentSettings[actionName] ?? ['Succeeded'];
    let newStatuses: RunAfterStatus[];

    if (checked) {
      // Avoid duplicates by checking if status already exists
      if (!currentStatuses.includes(status)) {
        newStatuses = [...currentStatuses, status];
      } else {
        // Status already selected, no change needed
        return;
      }
    } else {
      newStatuses = currentStatuses.filter((s) => s !== status);
      // Ensure at least one status is always selected
      if (newStatuses.length === 0) {
        newStatuses = ['Succeeded'];
      }
    }

    onChange({
      ...currentSettings,
      [actionName]: newStatuses,
    });
  };

  const handleRemoveDependency = (actionName: string) => {
    const newSettings = { ...currentSettings };
    delete newSettings[actionName];
    onChange(newSettings);
  };

  const handleAddDependency = (actionName: string) => {
    onChange({
      ...currentSettings,
      [actionName]: ['Succeeded'],
    });
    setIsAdding(false);
  };

  // Actions available to add: not already in dependencies
  const availableToAdd = availableActions.filter((action) => !dependencyNames.includes(action));

  return (
    <div className={styles.container}>
      {label && <Text className={styles.label}>{label}</Text>}

      {dependencyNames.length === 0 ? (
        <Text className={styles.emptyState}>
          No dependencies. Runs after trigger.
        </Text>
      ) : (
        <div className={styles.dependencyList}>
          {dependencyNames.map((actionName) => {
            const statuses = currentSettings[actionName] ?? ['Succeeded'];
            return (
              <div key={actionName} className={styles.dependencyItem}>
                <Text className={styles.dependencyName}>{actionName}</Text>
                <div className={styles.statusGroup}>
                  {RUN_AFTER_STATUSES.map((status) => (
                    <Checkbox
                      key={status.value}
                      label={status.label}
                      checked={statuses.includes(status.value)}
                      onChange={(_, data) =>
                        handleStatusChange(actionName, status.value, data.checked === true)
                      }
                      disabled={readOnly}
                    />
                  ))}
                </div>
                {!readOnly && (
                  <Button
                    appearance="subtle"
                    icon={<DismissRegular />}
                    size="small"
                    onClick={() => handleRemoveDependency(actionName)}
                    aria-label={`Remove dependency on ${actionName}`}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {!readOnly && availableToAdd.length > 0 && (
        isAdding ? (
          <Combobox
            className={styles.addButton}
            placeholder="Select action..."
            autoFocus
            onOptionSelect={(_e, data) => {
              if (data.optionValue) {
                handleAddDependency(data.optionValue);
              }
            }}
            onBlur={() => setIsAdding(false)}
          >
            {availableToAdd.map((action) => (
              <Option key={action} value={action}>{action}</Option>
            ))}
          </Combobox>
        ) : (
          <Button
            className={styles.addButton}
            appearance="subtle"
            icon={<AddRegular />}
            size="small"
            onClick={() => setIsAdding(true)}
          >
            Add dependency
          </Button>
        )
      )}
    </div>
  );
};

RunAfterRenderer.displayName = 'RunAfterRenderer';

// Register this renderer
registerSettingRenderer('runAfter', RunAfterRenderer);
